import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TraceClassDecorator } from '@sentry/angular';
import { SelectItem } from 'primeng/api/selectitem';
import { MedicalHistoryService } from 'src/app/services/medical-history.service';
import { PatientService } from 'src/app/services/patient.service';
import { UtilitiesService } from 'src/app/utilities/utilities.service';
import { medicalHistoryApiData, pastMedicalHitory_item, PatientMedicalHistoryEdit, SocialHistory, Social_History_Data } from '../../../../models/history';
import { ApiResult } from '../../../../models/PromiseResult';
import { PatientviewService } from '../../../../services/patientview/patientview.service';

@Component({
    selector: 'app-past-medical-history',
    templateUrl: './past-medical-history.component.html',
    styleUrls: [
        './past-medical-history.component.css',
        '../../../../../assets/css/custom-egd.css'
    ]
})
@TraceClassDecorator()
export class PastMedicalHistoryComponent implements OnInit {

    @Input("patient_guid") patient_guid: string = "";
    @Input("condition_description") condition_description: string = "";
    @Output() msgToSibling = new EventEmitter<medicalHistoryApiData>();

    medicalHistoryApiData: medicalHistoryApiData = this.medicalHistoryService.init_medicalHistoryApiData();
    temp_medicalHistoryApiData: medicalHistoryApiData = this.medicalHistoryService.init_medicalHistoryApiData();
    api_result: ApiResult = new ApiResult(true);
    social_history_data: SafeHtml = "";
    is_edit: boolean = false;


    constructor(
        private readonly patientViewService: PatientviewService,
        private readonly patientService: PatientService,
        private readonly medicalHistoryService: MedicalHistoryService,
        private readonly utilitiesService: UtilitiesService,
        private readonly domSanitizer: DomSanitizer
    ) { }

    async ngOnInit(): Promise<void> { }

    async init() {
        await this.fetchHistory();
        this.msgToSibling.emit(this.medicalHistoryApiData);
    }

    async fetchHistory(): Promise<void> {
        this.api_result = new ApiResult(true);
        this.api_result.Posting = true;
        this.medicalHistoryApiData.selected_past_medical_history_data = [];
        await this.patientService
            .GetHistoryDefaultList()
            .toPromise()
            .then((result) => {
                const family_history = result.data.family_history;
                const medical_history = result.data.medical_history;
                this.medicalHistoryApiData.other_data.family_history_options_data = family_history.map((e, i) => {
                    let e_new: SelectItem = { label: e["history_name"], value: e["history_name"] };
                    return e_new;
                });
                this.medicalHistoryApiData.past_medical_history_data = medical_history.map((e, i) => ({ id: e["id"], name: e["history_name"], selected: false }));
            }).catch((error: HttpErrorResponse) => {
                this.api_result.PromiseResult.success = false;
                this.api_result.Posting = false;
                this.api_result.ServerError = error.error;
            });

        await this.patientViewService
            .fetchHistory(this.patient_guid)
            .toPromise()
            .then((result) => {
                this.api_result.PromiseResult = result;
                if (result.success) {
                    this.process_history(result)
                }
            })
            .catch((error: HttpErrorResponse) => {
                this.api_result.PromiseResult.success = false;
                this.api_result.Posting = false;
                this.api_result.ServerError = error.error;
            });
        this.api_result.Posting = false;

    }

    shallow_copy(a) {
        return this.utilitiesService.shallow_copy(a);
    }

    process_history(result): void {
        const medical_conditions = result.data.data.medical_conditions;
        if (medical_conditions != null)
            this.medicalHistoryApiData.currentMedicalProblems = medical_conditions.current_medical_problems || "";
        if (result.data.data.p_medical_history != null) {
            const family_history = result.data.data.p_medical_history.family_history;
            const medical_history = result.data.data.p_medical_history.past_medical_history_data;
            const socialHistory = result.data.data.p_medical_history.social_history;
            const surgicalHistory = result.data.data.p_medical_history.past_surgical_history_data;

            this.medicalHistoryApiData.past_surgical_history_data = surgicalHistory || "";
            if (family_history != null && family_history.length > 0) {
                this.medicalHistoryApiData.selected_family_history = family_history;
                this.medicalHistoryApiData.family_history = this.medicalHistoryService.preprocess_familyhistory(family_history);
            }
            else {
                this.medicalHistoryApiData.family_history = this.medicalHistoryService.init_medicalHistoryApiData().family_history;
            }

            if (medical_history != null && medical_history.length > 0) {
                this.medicalHistoryApiData.selected_past_medical_history_data = medical_history.map((e) => {
                    let e_new = this.shallow_copy(e);
                    e_new["selected"] = true;
                    return e_new;
                });
                this.medicalHistoryApiData.other_data.none_selected_medical_history = false;
                this.medicalHistoryApiData.past_medical_history_data = this.medicalHistoryService.preprocess_medicalhistory(this.medicalHistoryApiData.past_medical_history_data, this.medicalHistoryApiData.selected_past_medical_history_data);
            }
            else {
                this.medicalHistoryApiData.past_medical_history_data = [];
                this.medicalHistoryApiData.selected_past_medical_history_data = [];
                this.medicalHistoryApiData.other_data.none_selected_medical_history = true;
            }

            if (socialHistory != null) {
                let a: Social_History_Data = this.medicalHistoryService.preprocess_socialhistory(socialHistory);
                this.social_history_data = a.social_history_data;
                this.medicalHistoryApiData.social_history = {
                    smoking: a.smoking,
                    alcohol: a.alcohol,
                }

            }
            else {
                this.medicalHistoryApiData.social_history.smoking = this.medicalHistoryService.init_Smoking();
                this.medicalHistoryApiData.social_history.alcohol = this.medicalHistoryService.init_Alcohol();
            }
        }
        if (result.data.data.medical_allergies != null) {
            const allergies = result.data.data.medical_allergies.medications_allergies_data.allergies;
            const medications = result.data.data.medical_allergies.medications_allergies_data.medications;


            if (allergies != null && allergies.length > 0) {
                this.medicalHistoryApiData.allergies = allergies;
            }
            else {
                this.medicalHistoryApiData.allergies = [];
            }
            if (medications != null && medications.length > 0) {
                this.medicalHistoryApiData.medications = medications.map((e) => {
                    let e_new = this.shallow_copy(e);
                    e_new["instructions"] = !Boolean(e["instructions"]) ? '' : e["instructions"];
                    return e_new;
                });
            }
            else {
                this.medicalHistoryApiData.medications = [];
            }
        }
    }

    getStringPastMedicalHistory(past_medical_history_data: pastMedicalHitory_item[]): string {
        let a: string = "";
        a = past_medical_history_data.filter(el =>
            el.selected
        ).map(ele => {
            return ele.name
        }).join(', ') || "N/A";
        return a;
    }

    getNAForEmptyString(a: SafeHtml | string): SafeHtml {
        return a || "N/A";
    }

    getNAForEmptyArray(a: any[]): string {
        return a.length == 0 ? "N/A" : "";
    }


    sanitizeString(s: string): SafeHtml {
        return this.domSanitizer.bypassSecurityTrustHtml(s);
    }
    // async GetHistoryList() {

    // }

    async savePatientMedicalHistory(data: PatientMedicalHistoryEdit) {
        await this.patientViewService
            .savePatientMedicalHistory(data)
            .toPromise()
            .then((result) => {
                this.api_result.PromiseResult = result;
                this.api_result.Posting = false;
                if (result.success) {
                    this.is_edit = false;
                }
            })
            .catch((error: HttpErrorResponse) => {
                this.api_result.PromiseResult.success = false;
                this.api_result.Posting = false;
                this.api_result.ServerError = error.error;
            });
    }

    edit() {
        this.temp_medicalHistoryApiData = this.medicalHistoryService.init_medicalHistoryApiData();
        this.temp_medicalHistoryApiData = this.shallow_copy(this.medicalHistoryApiData);
        this.is_edit = true;

    }


    cancel() {
        this.medicalHistoryApiData = this.shallow_copy(this.temp_medicalHistoryApiData);
        this.is_edit = false;
    }



    async save() {
        this.api_result.Posting = true;
        this.temp_medicalHistoryApiData = this.shallow_copy(this.medicalHistoryApiData);
        this.medicalHistoryApiData.selected_past_medical_history_data = this.medicalHistoryService.filterMedicalHistory(this.medicalHistoryApiData.past_medical_history_data);
        this.medicalHistoryApiData.selected_family_history = this.medicalHistoryService.filterFamilyHistory(this.medicalHistoryApiData.family_history);
        let b: Social_History_Data = this.medicalHistoryService.preprocess_socialhistory(this.medicalHistoryApiData.social_history);
        this.social_history_data = b.social_history_data;
        this.medicalHistoryApiData.social_history = {
            smoking: b.smoking,
            alcohol: b.alcohol,
        }
        let a: PatientMedicalHistoryEdit = new PatientMedicalHistoryEdit();
        a = {
            patient_guid: this.patient_guid,
            current_medical_problems: this.medicalHistoryApiData.currentMedicalProblems,
            past_surgical_history_data: this.medicalHistoryApiData.past_surgical_history_data,
            allergies: this.medicalHistoryApiData.allergies,
            medications: this.medicalHistoryApiData.medications,
            social_history: this.medicalHistoryApiData.social_history,
            family_history: this.medicalHistoryApiData.selected_family_history,
            past_medical_history_data: this.medicalHistoryApiData.selected_past_medical_history_data,
        };
        await this.savePatientMedicalHistory(a);
    }


    async processTheData(ApiData: medicalHistoryApiData) {
        this.medicalHistoryApiData = ApiData;
        // if (this.medicalHistoryApiData.other_data.is_exist_current_medical_problem == false)
        //     await this.CreateConditions();
        // else await this.UpdateMedicalConditions();
        // await this.submitHistory();
        // this.medicalHistoryApiData.other_data.empty_medical_history = this.medicalHistoryService.allMedicalSelectdCheck(this.medicalHistoryApiData.past_medical_history_data);
    }
}
